import * as Tooltip from '@radix-ui/react-tooltip';
import {layer} from '@wandb/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React from 'react';

import {type GraphNotices} from './graphRenderProps';
import {SAMPLING_FALLBACK_ERROR_MESSAGE} from './RunsLinePlotContext/useDetermineAggregationType';

const positions = ['bl', 'tl', 'br', 'tr'] as const;

export type SamplingWarningViewProps = {
  notices: Pick<GraphNotices, 'isSamplingFallback' | 'samplingWarnings'>;
  style?: React.CSSProperties;
  width?: number;
};

/**
 * Shows an info icon with a tooltip when a full-fidelity panel falls back to
 * sampling. Reads only its props.
 */
export function SamplingWarningView({
  notices,
  style,
  width = 100,
}: SamplingWarningViewProps) {
  // leaving this for now, would like to let users move this around to avoid it
  // overlaying important graph regions
  const [pos] = React.useState<(typeof positions)[number]>(positions[1]);
  const position = clsx('absolute inline-flex', {
    'top-4': pos.includes('t'),
    'left-[calc(50%-16px)]': pos.includes('l') && width > 250, // center the icon in the middle of panel header for large panels
    'left-[calc(30%)]': pos.includes('l') && width <= 250, // otherwise move it a bit to the left but not blocking the drag handle in the upper left corner
  });

  const pluralWarnings = (notices.samplingWarnings?.length ?? 0) > 1;

  if (!notices.isSamplingFallback) {
    return null;
  }

  // note: because the portal renders outside the heirarchy it needs its own Tailwind wrapper
  return (
    <Tailwind>
      <div className={position} style={style}>
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger className="inline-flex items-center justify-between gap-2 rounded bg-wb-surface-hover px-4 py-4 text-xs focus-visible:outline-none">
              <Icon name="info" height={14} />
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                sideOffset={8}
                collisionPadding={8}
                style={{zIndex: layer('tooltip')}}>
                <TailwindContents>
                  <div className="w-[340px] rounded bg-wb-surface-inverse p-12 text-sm leading-[18px] text-wb-text-inverse shadow-md">
                    {(notices.samplingWarnings?.length ?? 0) > 0 ? (
                      <div>
                        Full fidelity mode for point aggregation is in active
                        development. This panel is using sampling as the
                        following panel configuration
                        {pluralWarnings ? 's' : ''}{' '}
                        {pluralWarnings ? 'are' : 'is'} not yet supported:
                        <br />
                        <br />
                        {notices.samplingWarnings?.map((warning, i) => (
                          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                          <div key={i}>- {warning}</div>
                        ))}
                        <br />
                        Remove {pluralWarnings ? 'these' : 'this'} option
                        {pluralWarnings ? 's' : ''} to visualize points in full
                        fidelity.
                      </div>
                    ) : (
                      SAMPLING_FALLBACK_ERROR_MESSAGE
                    )}
                  </div>
                </TailwindContents>
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>
    </Tailwind>
  );
}
